/**
 * Slash Command Menu
 * Popup menu triggered by "/" for quick block/format insertion
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Editor } from '@tiptap/react';

interface SlashCommand {
  title: string;
  description: string;
  icon: string;
  action: (editor: Editor) => void;
}

const SLASH_COMMANDS: SlashCommand[] = [
  {
    title: 'Heading 1',
    description: 'Large section heading',
    icon: 'H1',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: 'H2',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: 'H3',
    action: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    title: 'Bullet List',
    description: 'Create a bulleted list',
    icon: '•',
    action: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    title: 'Numbered List',
    description: 'Create a numbered list',
    icon: '1.',
    action: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    title: 'Task List',
    description: 'Create a task list with checkboxes',
    icon: '☑',
    action: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    title: 'Blockquote',
    description: 'Add a quote block',
    icon: '"',
    action: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
  {
    title: 'Code Block',
    description: 'Add a code snippet',
    icon: '<>',
    action: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
  {
    title: 'Divider',
    description: 'Insert a horizontal rule',
    icon: '—',
    action: (editor) => editor.chain().focus().setHorizontalRule().run(),
  },
  {
    title: 'Image',
    description: 'Insert an image from URL',
    icon: '🖼',
    action: (editor) => {
      const url = window.prompt('Enter image URL:');
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    },
  },
  {
    title: 'Table',
    description: 'Insert a table',
    icon: '⊞',
    action: (editor) =>
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
  },
];

interface SlashCommandMenuProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
}

export function SlashCommandMenu({ editor, isOpen, onClose, position }: SlashCommandMenuProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredCommands = SLASH_COMMANDS.filter(
    (cmd) =>
      cmd.title.toLowerCase().includes(query.toLowerCase()) ||
      cmd.description.toLowerCase().includes(query.toLowerCase())
  );

  const executeCommand = useCallback(
    (command: SlashCommand) => {
      // Delete the "/" and any query text
      editor.chain().focus().deleteRange({
        from: editor.state.selection.from - query.length - 1,
        to: editor.state.selection.from,
      }).run();
      command.action(editor);
      onClose();
      setQuery('');
      setSelectedIndex(0);
    },
    [editor, query, onClose]
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Backspace' && query.length === 0) {
        onClose();
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        setQuery((prev) => prev + e.key);
      } else if (e.key === 'Backspace') {
        setQuery((prev) => prev.slice(0, -1));
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, filteredCommands, selectedIndex, query, executeCommand, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-50 bg-white rounded-lg shadow-xl border w-72 max-h-80 overflow-y-auto"
      style={{ top: position.top, left: position.left }}
    >
      {query && (
        <div className="px-3 py-2 border-b text-xs text-gray-500">
          Filtering: {query}
        </div>
      )}
      {filteredCommands.length === 0 ? (
        <div className="px-3 py-4 text-sm text-gray-500 text-center">No matching commands</div>
      ) : (
        filteredCommands.map((command, index) => (
          <button
            key={command.title}
            onClick={() => executeCommand(command)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
              index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
            }`}
          >
            <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded text-sm font-mono">
              {command.icon}
            </span>
            <div>
              <div className="text-sm font-medium">{command.title}</div>
              <div className="text-xs text-gray-500">{command.description}</div>
            </div>
          </button>
        ))
      )}
    </div>
  );
}
